<template>
  <section>
    <Tabs>
      <TabsHeader>
        <Tab
          v-for="(tab, index) in displayTabs"
          :key="'filterDetail' + tab"
          :index="index"
          :value="selectedTabIndex"
          :closable="false"
          @tabClick="(index) => changeTab(index)"
        >
          {{ tab }}<sup v-if="getTabDirtyStatus(tab)">*</sup>
        </Tab>
      </TabsHeader>
      <TabsBody>
        <TabPanelItem>
          <keep-alive exclude="HistoryTab,WhereUsedTab">
            <component
              :is="currentTabComponent"
              :client-id="currentClientId"
              :filter-id="currentFilterId"
              :code-meta-informations-id="currentFilterId"
              :read-only="readOnly"
              :use-data-source="true"
              :load-data="loadData"
              :server-side-datasource="getHistoryData"
              :history-data="filtersStore.historyData"
              :get-where-used-data="getWhereUsedData"
              :where-used-data="filtersStore.whereUsedData"
              :code-type="'Filter'"
              :name="name"
              :tab-name="tabName"
              :messages="getOverviewTabMessages"
              :code-meta-information-data="getCodeMetaData"
              :check-duplicate-short-name="checkDuplicateShortName"
              ref="componentRef"
              v-on="{ ...dynamicEventListner() }"
            />
          </keep-alive>
        </TabPanelItem>
      </TabsBody>
    </Tabs>
    <Modal
      :show="displaySaveModal"
      body-class="atb-border-none"
      footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
      @onModalClose="closeSaveModal"
    >
      <template #header> {{ $t('saveFilterTitle') }} </template>
      <template #body>
        <div class="atb-pb-2">{{ $t('saveRevisionTitle') }}</div>
        <div class="atb-flex atb-flex-col atb-gap-4">
          <Textarea
            id="filtersDetailsCodeRevisionCommentTextareaModal"
            v-model="comment"
            field-control-class="atb-h-24"
          />
        </div>
      </template>
      <template #footer>
        <Button
          id="filtersDetailsCodeRevisionCancelButtonModal"
          motif="basic"
          @click="closeSaveModal"
        >
          {{ $t('cancel') }}
        </Button>
        <Button
          id="filtersDetailsCodeRevisionSaveButtonModal"
          @click="displayConfirmationModal = true"
          :disabled="comment.trim() === ''"
          >{{ $t('save') }}</Button
        >
      </template>
    </Modal>
    <Modal
      :show="displayConfirmationModal"
      body-class="atb-border-none"
      footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
      @onModalClose="closeConfirmationModal"
    >
      <template
        #header
        class="atb-p-4"
      >
        {{ $t('areYouSure') }}
      </template>
      <template #body>
        <div class="atb-flex atb-flex-col atb-gap-4">
          {{ $t('checkingCodeSave') }}
        </div>
      </template>
      <template #footer>
        <Button
          id="filtersDetailsCodeRevisionConfirmationCancelButtonModal"
          motif="basic"
          @click="closeConfirmationModal"
          >{{ $t('cancel') }}</Button
        >
        <Button
          id="filtersDetailsCodeRevisionConfirmationSaveButtonModal"
          @click="saveConfirmationModal"
          >{{ $t('save') }}</Button
        >
      </template>
    </Modal>
  </section>
</template>

<script lang="ts" setup>
  import { useFiltersStore } from '@/app/features/ams/filters/filters-store';
  import CodeTab from '@/app/features/ams/shared/details-tab/CodeTab.vue';
  import HistoryTab from '@/app/features/ams/shared/details-tab/HistoryTab.vue';
  import OverviewTab from '@/app/features/ams/shared/details-tab/OverviewTab.vue';
  import ValidateTab from '@/app/features/ams/shared/details-tab/ValidateTab.vue';
  import WhereUsedTab from '@/app/features/ams/shared/details-tab/WhereUsedTab.vue';
  import { LoadLuaCodeEvent } from '@/app/features/ams/shared/types/events/code-tab-events.interface';
  import { CodeType } from '@/app/shared/enums/code-type.enum';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { useAuthStore } from '@/app/shared/stores/auth-store';
  import { CodeEditorLoadData } from '@/app/shared/types/code-editor-load-data.interface';
  import { AtbError } from '@/app/shared/types/errors/error.interface';
  import { AddTabEvent } from '@/app/shared/types/events/add-tab-event.interface';
  import { CodeMetaInformationDataEvent } from '@/app/shared/types/events/code-meta-information-data-event.interface';
  import { SaveFilterCodeEvent } from '@/app/shared/types/events/filters-event.interface';
  import { OverviewDataEvent } from '@/app/shared/types/events/overview-data-event.interface';
  import { ValidateFilterCodeEvent } from '@/app/shared/types/events/validate-tab-event.interface';
  import { Messages } from '@/app/shared/types/messages.interface';
  import { CheckCodeSyntaxPayload } from '@/app/shared/types/params/code-meta-information-params.interface';
  import { VersionControl } from '@/app/shared/types/version-control.interface';
  import { handleServerErrors } from '@/app/shared/utils/cmi-errors-handler.util';
  import { historyQueryParams } from '@/app/shared/utils/query-params-formatter.util';
  import { validateDuplicates } from '@/app/shared/utils/validation.util';
  import { useDataSourceSegmentStore } from '@/app/shared/stores/datasource-segment-store';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';
  import Tab from '@atb-ui/eds-vue/src/components/Tabs/Tab.vue';
  import TabPanelItem from '@atb-ui/eds-vue/src/components/Tabs/TabPanelItem.vue';
  import Tabs from '@atb-ui/eds-vue/src/components/Tabs/Tabs.vue';
  import TabsBody from '@atb-ui/eds-vue/src/components/Tabs/TabsBody.vue';
  import TabsHeader from '@atb-ui/eds-vue/src/components/Tabs/TabsHeader.vue';
  import Textarea from '@atb-ui/eds-vue/src/components/Textarea.vue';
  import { FeatureTabs } from '@/app/shared/enums/tab-names.enum';
  import { useAbility } from '@casl/vue';
  import { IGetRowsParams } from 'ag-grid-community';
  import { storeToRefs } from 'pinia';
  import {
    computed,
    nextTick,
    reactive,
    Ref,
    ref,
    shallowRef,
    toRefs,
    watch,
  } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  interface FilterDetailsEvent {
    (_event: 'refreshFiltersAfterDelete'): void;
    (_event: 'createNewTab', _params: AddTabEvent): void;
  }
  interface FilterDetailsProps {
    currentClientId: number;
    currentFilterId: number;
    tabName: string;
  }
  const emit = defineEmits<FilterDetailsEvent>();
  const props = defineProps<FilterDetailsProps>();
  const router = useRouter();

  const { t } = useI18n();
  const filtersStore = useFiltersStore();
  const alertStore = useAlertStore();
  const authStore = useAuthStore();
  const { can } = useAbility();
  const { currentClientId, currentFilterId, tabName } = toRefs(props);
  const { filterDetailsMap } = storeToRefs(filtersStore);
  const selectedTabIndex = ref(0);
  const loadData: Ref<CodeEditorLoadData> = ref({
    code: '',
    codeType: '',
    shortName: '',
    longName: '',
    description: '',
    initializationValue: '',
    defaultValue: '',
    format: '',
    readonly: false,
  });
  const comment = ref('');
  const payload = ref({} as CheckCodeSyntaxPayload);
  const overviewTabMessages = ref({} as Messages);
  const displaySaveModal = ref(false);
  const displayConfirmationModal = ref(false);
  let CodeMetaInformationDataRef = ref({} as CodeMetaInformationDataEvent);
  const tabs = reactive([
    t('overview'),
    t('code'),
    t('validate'),
    t('history'),
    t('whereUsed'),
  ]);
  // TODO: Create an interface for this attribute or use an existing one
  const currentTabComponent: Ref<any> = shallowRef(OverviewTab);
  const componentRef = ref();
  const displayTabs = computed(() => {
    return getNewStatus.value ? tabs.slice(0, 1) : tabs;
  });
  const dataSourceSegmentStore = useDataSourceSegmentStore();

  const getCodeMetaData = computed(() => {
    const { overviewData, isCopied } = filtersStore.filterDetailsMap.get(
      currentFilterId.value
    );
    CodeMetaInformationDataRef.value.OverviewDataEvent = overviewData;
    CodeMetaInformationDataRef.value.isDirty = getTabDirtyStatus(tabName.value);
    CodeMetaInformationDataRef.value.isNew = getNewStatus.value;
    CodeMetaInformationDataRef.value.isCopied = isCopied?.isCopy;
    return CodeMetaInformationDataRef;
  });

  const getOverviewTabMessages = computed(() => {
    overviewTabMessages.value.checkingDeletion = t('checkingFilterDeletion');
    overviewTabMessages.value.checkingSave = t('checkingFilterSave');
    overviewTabMessages.value.nameInvalid = t('filtersNameInvalid');
    return overviewTabMessages;
  });

  const getTabDirtyStatus = (dirtyTabName: string) => {
    const isTabDirty = filtersStore.getTabDirtyStatus(
      currentFilterId.value,
      dirtyTabName
    );
    return typeof isTabDirty !== 'undefined' ? isTabDirty : false;
  };

  const getNewStatus = computed(() => {
    return filtersStore.filterDetailsMap.get(currentFilterId.value)?.isNew;
  });

  const readOnly = computed(() => {
    const filter = filtersStore.filterDetailsMap.get(
      Number(currentFilterId.value)
    );
    const versionControl: VersionControl = filter?.overviewData.versionControls
      .filter((vc: VersionControl) => vc.dataSource == null)
      .shift();
    const isAdmin = can('WRITE', 'ALL');
    const isLockedByCurrentUser =
      versionControl?.lockedByUsername?.toLowerCase() ===
      authStore.username.split('@').shift()?.toLowerCase();
    const isLockedBySomeone =
      versionControl?.lockedByUserId ?? versionControl?.autoLockedByUserId;
    if (isAdmin || isLockedByCurrentUser) {
      return false;
    } else {
      return isLockedBySomeone;
    }
  });

  watch(
    () => getNewStatus.value,
    (newValue) => {
      if (newValue === false) {
        currentTabComponent.value = CodeTab;
        selectedTabIndex.value = 1;
      }
    },
    { immediate: true }
  );

  const changeTab = async (index: number) => {
    selectedTabIndex.value = index;
    switch (index) {
      case FeatureTabs.OVERVIEW_TAB:
        currentTabComponent.value = OverviewTab;
        break;
      case FeatureTabs.CODE_TAB:
        currentTabComponent.value = CodeTab;
        break;
      case FeatureTabs.VALIDATE_TAB:
        currentTabComponent.value = ValidateTab;
        break;
      case FeatureTabs.HISTORY_TAB:
        currentTabComponent.value = HistoryTab;
        break;
      case FeatureTabs.WHERE_USED_TAB:
        currentTabComponent.value = WhereUsedTab;
        break;

      default:
        break;
    }
  };

  const deleteFilterFromOverview = async () => {
    try {
      await filtersStore.deleteFilters(currentClientId.value, [
        currentFilterId.value,
      ]);
      filtersStore.deleteFilterTab(currentFilterId.value);
      emit('refreshFiltersAfterDelete');
    } catch (e) {
      const errorResponse = e as AtbError;
      alertStore.showNotification(
        handleServerErrors(t, errorResponse?.message),
        'Filters',
        Motif.ERROR
      );
    }
  };

  const createNewTab = async (addTab: AddTabEvent) => {
    if (addTab.codeType?.toUpperCase() === CodeType.FILTER) {
      try {
        await filtersStore.lockFilterOrDs({
          clientId: currentClientId.value,
          filterId: Number(addTab.id),
          isAutoLock: true,
        });
        filtersStore.updateAutoLockStatusForTab(addTab.id, true);
      } catch (error) {
        console.error('error in locking filter id', addTab.id);
      }
      emit('createNewTab', {
        clientId: currentClientId.value,
        name: addTab.name,
        id: addTab.id,
        isNew: addTab.isNew,
        dirtyTab: addTab.dirtyTab,
      });
    } else {
      router.push({
        name: 'attributes',
        query: {
          attributeId: addTab.id,
          attributeName: addTab.name,
        },
      });
    }
  };

  const loadLuaCode = async ({ newDataSource }: LoadLuaCodeEvent) => {
    const { overviewData } = filterDetailsMap.value.get(currentFilterId.value);
    const dataSource = overviewData.versionControls
      .filter((vc: VersionControl) =>
        validateDuplicates(vc.dataSource?.name, newDataSource)
      )
      .shift();
    await filtersStore.fetchFilterById(
      currentClientId.value,
      Number(currentFilterId.value)
    );

    updateLoadData(dataSource, overviewData, newDataSource);
  };
  // TODO: Create an interface for this attribute or use an existing one
  const updateLoadData = async (
    dataSource: any,
    overviewData: any,
    newDataSource: string
  ) => {
    await dataSourceSegmentStore.fetchDataSourceSegmentList(
      currentClientId.value,
      newDataSource
    );
    loadData.value = {
      code: dataSource?.code,
      segment: dataSource?.segmentName,
      arguments: dataSource?.arguments,
      codeType: overviewData?.codeType || '',
      shortName: overviewData?.shortName || '',
      longName: overviewData?.longName || '',
      description: overviewData?.description || '',
      initializationValue: overviewData?.initializationValue || '',
      defaultValue: overviewData?.defaultValue || '',
      format: overviewData?.format || '',
      readonly: validateDataSourceAccess(newDataSource),
      availableSegments: dataSourceSegmentStore.dataSourceSegmentList,
    };
  };

  const validateDataSourceAccess = (newDataSource: string) => {
    const filter = filtersStore.filterDetailsMap.get(
      Number(currentFilterId.value)
    );
    const versionControl: VersionControl = filter?.overviewData.versionControls
      .filter((vc: VersionControl) =>
        validateDuplicates(vc.dataSource?.name, newDataSource)
      )
      .shift();
    const isAdmin = can('WRITE', 'ALL');
    const isDataSourceLockedByUser =
      versionControl.lockedByUsername?.toLowerCase() ===
      authStore.username.split('@').shift()?.toLowerCase();

    const isDataSourceLockedBySomeone =
      (versionControl?.lockedByUserId || versionControl?.autoLockedByUserId) &&
      versionControl?.lockedByUsername?.toLowerCase() !==
        authStore.username.split('@').shift()?.toLowerCase()
        ? true
        : false;

    if (isAdmin || isDataSourceLockedByUser) {
      return false;
    } else {
      return isDataSourceLockedBySomeone;
    }
  };

  const name = computed(() => {
    return filtersStore.filterDetailsMap.get(currentFilterId.value)?.filterName;
  });

  const rollback = async (
    rollbackRevisionId: number,
    chosenDataSourceName: string
  ) => {
    try {
      await filtersStore.rollbackFilterCode(
        currentClientId.value,
        rollbackRevisionId
      );
      await filtersStore.fetchFilterById(
        currentClientId.value,
        Number(currentFilterId.value)
      );
      filtersStore.setOverviewDataById(
        currentFilterId.value,
        filtersStore.newFilter
      );
      alertStore.showNotification(
        t('successfulFilterRollback'),
        'Filters',
        Motif.SUCCESS
      );
      codeEditorRefreshAfterRollbackHandler(chosenDataSourceName);
    } catch (error: any) {
      alertStore.showNotification(
        t('rollbackError') + error.errors[0].message,
        'Filters',
        Motif.ERROR
      );
    }
  };

  const codeEditorRefreshAfterRollbackHandler = async (
    newDataSource: string
  ) => {
    changeTab(FeatureTabs.CODE_TAB);
    const { overviewData } = filterDetailsMap.value.get(currentFilterId.value);
    const dataSource = overviewData.versionControls
      .filter((vc: VersionControl) =>
        validateDuplicates(vc.dataSource?.name, newDataSource)
      )
      .shift();
    await updateLoadData(dataSource, overviewData, newDataSource);
    await nextTick();
    componentRef.value.refreshCodeEditorOnRollback(newDataSource);
  };

  const saveOverviewData = async (filterOverviewData: OverviewDataEvent) => {
    try {
      if (filterOverviewData.id) {
        await filtersStore.updateFilter(
          currentClientId.value,
          currentFilterId.value,
          filterOverviewData
        );
        setDirtyField(t('overview'), false);
        displaySuccessMessage(t('filterSaved'));
      } else {
        const { isCopy, id } = filtersStore.filterDetailsMap.get(
          currentFilterId.value
        )?.isCopied ?? { isCopy: false, id: -1 };
        if (isCopy) {
          await filtersStore.copyFilter(
            filterOverviewData,
            currentClientId.value,
            id
          );
          filtersStore.setTabIsCopyStatusToDefaultById(currentFilterId.value);
          filtersStore.saveCreatedFilter(
            currentFilterId.value,
            filtersStore.newFilter as any
          );
          displaySuccessMessage(t('filterSaved'));
        } else {
          await filtersStore.createFilter(
            currentClientId.value,
            filterOverviewData
          );
          filtersStore.saveCreatedFilter(
            currentFilterId.value,
            filtersStore.newFilter as any
          );
          displaySuccessMessage(t('filterSaved'));
        }
      }
    } catch (error) {
      const errorResponse = error as AtbError;
      alertStore.showNotification(
        handleServerErrors(t, errorResponse?.errors[0].message),
        'Filters',
        Motif.ERROR
      );
    }
  };

  const setDirtyField = (dirtyTabName: string, isDirty: boolean) => {
    filtersStore.setTabDirtyFieldById(
      currentFilterId.value,
      dirtyTabName,
      isDirty
    );
  };

  const displaySuccessMessage = (message: string) => {
    alertStore.showNotification(message, 'Filters', Motif.SUCCESS);
  };

  const saveCodeInVersionControls = async ({
    code: codeToSave,
    dataSourceId,
    segment,
    filterArguments,
  }: SaveFilterCodeEvent) => {
    try {
      const { overviewData } = filterDetailsMap.value.get(
        currentFilterId.value
      );
      const body = {
        luaCode: codeToSave,
        segmentName: segment,
        arguments: filterArguments,
        dataSourceId,
        isStagg: overviewData.isStagg,
        shortName: overviewData.shortName,
      };
      await filtersStore.checkCodeSyntax(currentClientId.value, body);
      filtersStore.updateCodeInVersionControlsById(
        Number(currentFilterId.value),
        codeToSave
      );
      displaySaveModal.value = true;
      payload.value = body;
    } catch (error) {
      const message = error as AtbError;
      alertStore.showNotification(
        message?.errors?.[0].error || 'Unknown error returned from server',
        'Filters',
        Motif.ERROR
      );
    }
  };

  const saveConfirmationModal = async () => {
    try {
      payload.value.comments = comment.value;
      await filtersStore.saveFilterCode(
        currentClientId.value,
        Number(currentFilterId.value),
        payload.value
      );
      setDirtyField(t('code'), false);
      displayConfirmationModal.value = false;
      alertStore.showNotification(t('filterSaved'), 'Filters', Motif.SUCCESS);
      comment.value = '';
    } catch (error) {
      displaySaveModal.value = false;
      displayConfirmationModal.value = false;
      const message = error as AtbError;
      alertStore.showNotification(
        message?.errors?.[0].error || 'Unkown error returned from server',
        'Filters',
        Motif.ERROR
      );
    }
    try {
      await filtersStore.fetchFilterById(
        currentClientId.value,
        Number(currentFilterId.value)
      );
      filtersStore.setOverviewDataById(
        currentFilterId.value,
        filtersStore.newFilter
      );
      displaySaveModal.value = false;
    } catch (error) {
      const message = error as AtbError;
      alertStore.showNotification(
        message?.errors?.[0].error || 'Error retrieving overview data',
        'Filters',
        Motif.ERROR
      );
    }
  };

  const closeSaveModal = () => {
    displaySaveModal.value = false;
    comment.value = '';
  };

  const closeConfirmationModal = () => {
    displayConfirmationModal.value = false;
  };

  const dynamicEventListner = () => {
    if (selectedTabIndex.value === 0) {
      return {
        codeMetaInformationsDeleted: deleteFilterFromOverview,
        saveOverviewData: saveOverviewData,
        setDirtyStatus: setDirtyField,
      };
    } else if (selectedTabIndex.value === 1) {
      return {
        loadLuaCode: loadLuaCode,
        saveCode: saveCodeInVersionControls,
        setDirtyStatus: setDirtyField,
        autoLockDataSource: autoLockDataSource,
      };
    } else if (selectedTabIndex.value === 4) {
      return { createNewTab: createNewTab };
    } else if (selectedTabIndex.value === 3) {
      return { rollbackCode: rollback };
    } else if (selectedTabIndex.value === 2) {
      return { arguments: loadArguments, validateCode: validateCode };
    }
  };

  const autoLockDataSource = async (params: {
    dataSourceName: string;
    filterId: number;
  }) => {
    const { dataSourceName, filterId } = params;
    await filtersStore
      .lockFilterOrDs({
        clientId: currentClientId.value,
        filterId,
        isAutoLock: true,
        dataSourceName,
      })
      .catch((e) => {
        displayError(e);
      });
  };

  const displayError = (error: any) => {
    alertStore.showNotification(
      error.errors[0].message,
      t('attributes'),
      Motif.ERROR
    );
  };

  const getHistoryData = (
    currentPage: number,
    rowsPerPage: number,
    calculatePagination: (totalElements: number) => void
  ) => {
    return {
      getRows: async ({
        sortModel,
        successCallback,
        failCallback,
      }: IGetRowsParams) => {
        try {
          componentRef.value.gridApi.value?.showLoadingOverlay();
          await filtersStore.fetchHistoryDataById(
            currentClientId.value,
            Number(currentFilterId.value),
            historyQueryParams(sortModel, {
              page: currentPage > 0 ? currentPage - 1 : 0,
              size: rowsPerPage,
            })
          );
          if (filtersStore.historyData.numberOfElements > 0) {
            componentRef.value.gridApi.value?.hideOverlay();
          } else {
            componentRef.value.gridApi.value?.showNoRowsOverlay();
          }
          calculatePagination(filtersStore.historyData.totalElements);
          successCallback(
            filtersStore.historyData.content || [],
            filtersStore.historyData.numberOfElements || 0
          );
        } catch (_error) {
          alertStore.showNotification(
            'Error fetching history list',
            '',
            Motif.ERROR
          );
          failCallback();
        }
      },
    };
  };

  const getWhereUsedData = (
    currentPage: number,
    rowsPerPage: number,
    calculatePagination: (totalElements: number) => void
  ) => {
    return {
      getRows: async ({ successCallback, failCallback }: IGetRowsParams) => {
        try {
          componentRef.value.gridApi.value?.showLoadingOverlay();
          await filtersStore.fetchWhereUsedDetails(
            currentClientId.value,
            Number(currentFilterId.value)
          );
          if (filtersStore.whereUsedData.length === 0)
            return componentRef.value.gridApi.value?.showNoRowsOverlay();
          componentRef.value.gridApi.value?.hideOverlay();
          const offsetStart = (currentPage - 1) * rowsPerPage;
          const offsetEnd = currentPage * rowsPerPage;
          calculatePagination(filtersStore.whereUsedData.length);
          const result = filtersStore.whereUsedData.slice(
            offsetStart,
            offsetEnd
          );
          successCallback(result, filtersStore.whereUsedData.length);
        } catch (_error) {
          alertStore.showNotification(
            'Error fetching where used list',
            '',
            Motif.ERROR
          );
          failCallback();
        }
      },
    };
  };

  const validateCode = async ({
    dataSourceName,
    file,
    argumentsInput,
  }: ValidateFilterCodeEvent) => {
    try {
      await filtersStore.validateFilterCode(
        currentClientId.value,
        Number(currentFilterId.value),
        dataSourceName,
        file,
        argumentsInput
      );
      const warningMessage = filtersStore.filterValidate.WARNING;
      const inputData = filtersStore.filterValidate.FILTER;
      componentRef.value.setArfs(filtersStore.filterValidate.ARFS);

      if (warningMessage) {
        alertStore.showNotification(warningMessage, 'Warning', Motif.ERROR);
      }
      componentRef.value.transformData(inputData, CodeType.FILTER);
      componentRef.value.toggleIsDataDisplayed(true);
    } catch (error) {
      const errorResponse = error as AtbError;
      componentRef.value.toggleIsDataDisplayed(false);
      alertStore.showNotification(
        errorResponse?.errors?.[0].error,
        'Warning',
        Motif.ERROR
      );
      console.error('error validating attribute code: ', error);
    }
  };

  const loadArguments = async (selectedDataSourceName: string) => {
    await filtersStore.argumentsFilter(
      currentClientId.value,
      Number(currentFilterId.value),
      selectedDataSourceName
    );
    const filterArguments = filtersStore.filterArgument['arguments-count'];
    if (filterArguments > 0) {
      componentRef.value.setArgumentValue(filterArguments);
      componentRef.value.toggleisFilterArgument(true);
    } else {
      componentRef.value.toggleisFilterArgument(false);
    }
  };

  const checkDuplicateShortName = async (shortName: string) => {
    try {
      await filtersStore.checkDuplicateShortName(
        currentClientId.value,
        shortName
      );
      return true;
    } catch (e: any) {
      alertStore.showNotification(
        e.errors[0].message,
        t('filterAlertTitle'),
        Motif.ERROR
      );
      return false;
    }
  };
</script>
